import { KeyRound, Leaf, ShieldCheck } from "lucide-react";
import { useState, type FormEvent } from "react";
import { ApiProblem, changePassword, signOut } from "../lib/api";

interface RequiredPasswordChangePageProps {
  readonly initialCurrentPassword?: string;
  readonly onCompleted: () => void;
  readonly onSignedOut: () => void;
}

export function RequiredPasswordChangePage({
  initialCurrentPassword = "",
  onCompleted,
  onSignedOut,
}: RequiredPasswordChangePageProps) {
  const [currentPassword, setCurrentPassword] = useState(
    initialCurrentPassword,
  );
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (newPassword === currentPassword) {
      setError("Choose a password different from the temporary password.");
      return;
    }
    if (newPassword !== confirmation) {
      setError("The new password confirmation does not match.");
      return;
    }
    setSubmitting(true);
    setError(undefined);
    try {
      await changePassword(currentPassword, newPassword);
      await signOut();
      onCompleted();
    } catch (failure) {
      setError(
        failure instanceof ApiProblem
          ? failure.problem.detail
          : "The password could not be changed. Check your connection and try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function leave() {
    setSubmitting(true);
    try {
      await signOut();
    } finally {
      onSignedOut();
    }
  }

  return (
    <main className="login-shell">
      <section className="login-context" aria-labelledby="rotation-heading">
        <div className="login-brand">
          <span className="brand-mark" aria-hidden="true">
            <Leaf />
          </span>
          <span>BalikTANiman</span>
        </div>
        <p className="login-kicker">Account security</p>
        <h1 id="rotation-heading">Replace the temporary password.</h1>
        <p className="login-lead">
          Your account is ready, but normal staff data and actions stay locked
          until you choose a private password.
        </p>
        <div className="login-assurance">
          <ShieldCheck aria-hidden="true" />
          <p>
            <strong>Every session will be closed</strong>
            <span>Sign in again with the new password when this is done.</span>
          </p>
        </div>
      </section>
      <section className="login-panel" aria-labelledby="rotation-form-title">
        <div className="login-card">
          <span className="login-lock" aria-hidden="true">
            <KeyRound />
          </span>
          <h2 id="rotation-form-title">Set your password</h2>
          <p>Use at least 12 characters that are not the temporary password.</p>
          <form onSubmit={(event) => void submit(event)}>
            <label htmlFor="rotation-current-password">
              Temporary password
            </label>
            <input
              id="rotation-current-password"
              type="password"
              autoComplete="current-password"
              minLength={12}
              required
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
            <label htmlFor="rotation-new-password">New password</label>
            <input
              id="rotation-new-password"
              type="password"
              autoComplete="new-password"
              minLength={12}
              required
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
            <label htmlFor="rotation-confirm-password">
              Confirm new password
            </label>
            <input
              id="rotation-confirm-password"
              type="password"
              autoComplete="new-password"
              minLength={12}
              required
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
            />
            {error === undefined ? null : (
              <p className="login-error" role="alert">
                {error}
              </p>
            )}
            <button
              className="login-submit"
              type="submit"
              disabled={submitting}
            >
              {submitting ? "Changing password…" : "Change password"}
            </button>
            <button
              className="staff-action rotation-sign-out"
              type="button"
              disabled={submitting}
              onClick={() => void leave()}
            >
              Sign out
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}
